// Demo fixture data for designer / screenshot mode of the Nexus Dex prototype.
//
// PRODUCTION SAFETY: every export below is wrapped in an
// `import.meta.env.PROD ? <empty> : <demo>` ternary. Vite + terser eliminate
// the dead branch at build time, so production bundles ship with empty
// fixtures and zero hardcoded species names, events, or stats.
//
// New consumers MUST handle the empty case (KPIS.main.caught === 0,
// EVENTS.length === 0, etc.) and render a proper empty-state UI in
// production. Do not assume non-empty.

export type DexStatus = "caught" | "seen" | "missing" | "locked";
export type DexCategory = "main" | "shiny" | "lucky" | "perfect" | "purified" | "xxl" | "xxs";

export interface DexEntry {
  id: number;          // National dex number (used for ordering/labels only)
  status: DexStatus;
  isNew?: boolean;
  hue: number;         // representative hue for the abstract orb
  generation: number;
  // PR-E2b: variant-axis form id (e.g. 'vivillon-meadow'). Optional;
  // when present this entry represents a specific form rather than the
  // base species. PR-E2b ships no UI that reads this field; PR-E2c
  // wires the sub-dex screen against it.
  formId?: string;
}

import { NATIONAL_DEX_TOTAL, RELEASED_IN_GO as RELEASED_IN_GO_DEFAULT } from "./constants";


// Re-exported for existing consumers. No longer hardcoded as 914 — that smaller
// denominator produced >100% completion bars once a real caught count exceeded
// it. The denominator of last resort is now the National Dex ceiling; live
// server totals still take precedence wherever they are available.
export const RELEASED_IN_GO = RELEASED_IN_GO_DEFAULT;

export interface KpiCategory {
  caught: number;
  total: number;
  color: string;
}

export interface KpiMap {
  main: KpiCategory;
  shiny: KpiCategory;
  lucky: KpiCategory;
  perfect: KpiCategory;
}

const EMPTY_KPIS: KpiMap = {
  main:    { caught: 0, total: RELEASED_IN_GO, color: "hsl(var(--primary))" },
  shiny:   { caught: 0, total: RELEASED_IN_GO, color: "hsl(var(--shiny))" },
  lucky:   { caught: 0, total: RELEASED_IN_GO, color: "hsl(var(--lucky))" },
  perfect: { caught: 0, total: RELEASED_IN_GO, color: "hsl(var(--perfect))" },
};

export const KPIS: KpiMap = import.meta.env.PROD ? EMPTY_KPIS : {
  main:    { caught: 847, total: RELEASED_IN_GO, color: "hsl(var(--primary))" },
  shiny:   { caught: 312, total: RELEASED_IN_GO, color: "hsl(var(--shiny))" },
  lucky:   { caught: 186, total: RELEASED_IN_GO, color: "hsl(var(--lucky))" },
  perfect: { caught: 44,  total: RELEASED_IN_GO, color: "hsl(var(--perfect))" },
};

// Generate a deterministic dex grid with a realistic distribution.
function rand(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}


// "Discovered" simulated scan results, short, punchy
export interface ScanResult {
  id: number;
  label: string;          // generic placeholder e.g. "#0143"
  status: DexStatus;
  hue: number;
}


// Events
export interface GameEvent {
  id: string;
  date: string;          // ISO-ish display label
  start: string;         // YYYY-MM-DD
  end?: string;
  title: string;
  category:
    | "community-day"
    | "raid"
    | "raid-day"
    | "raid-hour"
    | "event"
    | "spotlight"
    | "max-battle"
    | "max-monday"
    | "hatch-day"
    | "pvp"
    | "research"
    | "season"
    | "league"
    | "go-fest"
    | "go-pass";
  blurb: string;
  featured?: boolean;
  image?: string;        // optional event artwork (live feed) for the day strip
  // PR-E10 fix 1: per-event LeekDuck deep-link. Populated from the live feed
  // via liveToGameEvent (LiveEventApi.link). When absent the UI falls back
  // to the LeekDuck events index so demo / hardcoded entries still resolve.
  link?: string;
  // The species this event features, threaded through from the live feed
  // (LiveEventApi.spawns) so the Calendar can cross-reference them against the
  // trainer's own dex ("what am I still missing from this event?"). Absent on
  // the hardcoded demo entries below, which carry no species links.
  spawns?: EventSpawn[];
  // Bonus lines the feed states for this event ("2× Catch Candy"), threaded
  // through from the live feed (LiveEventApi.bonuses) so event cards can badge
  // what the event is worth playing for. Parsed/merged with the curated
  // registry by lib/eventBonuses.ts.
  bonuses?: string[];
  /**
   * A one-place event (a City Safari, a Wild Area city, a Night Out), from
   * the worker's lib/eventGeo (2026-10-23). The feed only lists such an event
   * in Now / Upcoming when this device is attending it (lib/eventAttending);
   * the Calendar's "In person" section lists every one with the switch.
   */
  geoLocked?: boolean;
  /** The city the name states ("Boston", "Sendai • Tohoku"), when it does. */
  place?: string;
}

/**
 * One species an event features, as served by /api/events/live. `speciesId` is
 * absent when LeekDuck named a Pokémon we could not resolve to a dex number —
 * such a spawn is display-only and never cross-references. `variants` is present
 * only on admin-linked events and pins the exact dex variants that event
 * features for the species.
 */
export interface EventSpawn {
  name?: string;
  speciesId?: number;
  shinyBoost?: boolean;
  /** Auto-detected form suffix from the feed ("armored", "galar"), see raidDexCrossRef. */
  formSuffix?: string;
  variants?: string[];
  /**
   * Specific forms/costumes of this species the event features, from an admin
   * event-link override (migration 0078) — e.g. the WCS 2026 costume Pikachu
   * in Main + Shiny. `formId` is the FormId slug of pokemonForms.generated.ts.
   * With forms present, EMPTY `variants` means the admin linked only the
   * forms: there is no species-level chase to fall back to.
   */
  forms?: Array<{ formId: string; variants?: string[] }>;
}

export const EVENTS: GameEvent[] = import.meta.env.PROD ? [] : [
  { id: "regidrago",   start: "2026-04-01", end: "2026-04-07", date: "Apr 1, 7",   title: "Regidrago, T5 Raids",       category: "raid",  blurb: "Dragon-type pseudo-legendary raid hour featured." },
  { id: "kyogre",      start: "2026-04-08", end: "2026-04-14", date: "Apr 8, 14",  title: "Kyogre, T5 Raids",          category: "raid",  blurb: "Water-type weather boost windows favor coastal raids." },
  { id: "tinkatink-cd",start: "2026-04-11", end: "2026-04-11", date: "Apr 11, 2, 5pm", title: "Tinkatink Community Day", category: "community-day", featured: true, blurb: "Featured shiny window, your best Shiny Tinkaton shot this season." },
  { id: "sustain",     start: "2026-04-14", end: "2026-04-20", date: "Apr 14, 20", title: "Sustainability Week",        category: "event", blurb: "Earth-, Grass- and Steel-themed spawns and field research." },
  { id: "groudon",     start: "2026-04-15", end: "2026-04-21", date: "Apr 15, 21", title: "Groudon, T5 Raids",         category: "raid",  blurb: "Ground-type legendary raid window." },
  { id: "riolu",       start: "2026-04-18", end: "2026-04-18", date: "Apr 18",      title: "Riolu Hatch Day",            category: "hatch-day", blurb: "2km eggs feature Riolu, increased shiny rate." },
  { id: "tapukoko",    start: "2026-04-22", end: "2026-04-28", date: "Apr 22, 28", title: "Tapu Koko, T5 Raids",       category: "raid",  blurb: "Electric/Fairy guardian, debut of regional kahuna boss." },
  { id: "max-battle",  start: "2026-04-25", end: "2026-04-25", date: "Apr 25",      title: "Max Battle Day",             category: "max-battle", blurb: "Power Spots refresh more frequently, stack Max Particles." },
  { id: "steeled",     start: "2026-04-28", end: "2026-05-04", date: "Apr 28, May 4", title: "Steeled Resolve",         category: "event", blurb: "Steel-type spawns increase, debut of new Galarian forms." },
  { id: "tapulele",    start: "2026-04-29", end: "2026-05-05", date: "Apr 29, May 5", title: "Tapu Lele, T5 Raids",    category: "raid",  featured: true, blurb: "Psychic/Fairy guardian, missing from your Main Dex." },
  { id: "taken-over",  start: "2026-04-30", end: "2026-05-04", date: "Apr 30, May 4", title: "Steeled Resolve: Taken Over", category: "event", blurb: "Shadow Raids feature event-themed bosses." },
  // Carries demo bonus lines so the badge row (BonusBadges) shows up in
  // design-QA screenshots the way a real Community Day's feed entry would.
  { id: "lechonk-cd",  start: "2026-05-09", end: "2026-05-09", date: "May 9, 2, 5pm", title: "Lechonk Community Day",   category: "community-day", blurb: "Pig-type evolves into Oinkologne with exclusive move.", bonuses: ["3× Catch Stardust", "2× Catch Candy"] },
  { id: "may16-cd",    start: "2026-05-16", end: "2026-05-16", date: "May 16, 2, 5pm", title: "Community Day Classic",  category: "community-day", blurb: "Returning fan-favorite legacy move." },
];


// Focus plans
export interface FocusItem { text: string; done?: boolean; }
export interface FocusPlan {
  id: string;
  emoji: string;
  date: string;
  title: string;
  category: DexCategory;
  why: string;
  items: FocusItem[];
  priority: "high" | "med" | "low";
}


